/**
 * PricingCard Component
 *
 * Displays a subscription tier with pricing, features, and CTA.
 * Responsive: compact on mobile, spacious on web.
 *
 * NOTE: CTA button uses View wrapper + Pressable inside (not Pressable
 * with callback style) because RN 0.81 Pressable with style={({pressed}) => ...}
 * doesn't compute height correctly on mobile in some layout contexts.
 */

import React from 'react';
import { View, Text, Pressable } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Check, Crown, Sparkles, Zap } from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import { isWeb } from '@/lib/platform';
import type { TierConfig, BillingInterval } from '@/lib/subscription/types';
import { formatPrice } from '@/lib/subscription/pricing';

interface PricingCardProps {
  tier: TierConfig;
  billingInterval: BillingInterval;
  isCurrentPlan: boolean;
  currentTierPrice?: number;
  onSelect: () => void;
  disabled?: boolean;
  index?: number;
  /** Stretch to equal height + pin the CTA to the bottom (for side-by-side layouts). */
  equalHeight?: boolean;
}

function TierBadge({ type }: { type: 'popular' | 'best-value' }) {
  if (type === 'popular') {
    return (
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, backgroundColor: '#00B8B8', paddingHorizontal: 12, paddingVertical: 6, borderRadius: 20 }}>
        <Sparkles size={12} color="#fff" />
        <Text style={{ color: '#fff', fontSize: 12, fontWeight: '600' }}>Most Popular</Text>
      </View>
    );
  }
  return (
    <LinearGradient colors={['#F59E0B', '#D97706']} start={{ x: 0, y: 0 }} end={{ x: 1, y: 0 }} style={{ flexDirection: 'row', alignItems: 'center', gap: 4, paddingHorizontal: 12, paddingVertical: 6, borderRadius: 20 }}>
      <Crown size={12} color="#fff" />
      <Text style={{ color: '#fff', fontSize: 12, fontWeight: '600' }}>Best Value</Text>
    </LinearGradient>
  );
}

export function PricingCard({
  tier,
  billingInterval,
  isCurrentPlan,
  currentTierPrice = 0,
  onSelect,
  disabled = false,
  equalHeight = false,
}: PricingCardProps) {
  const colors = useThemeColors();
  const pad = isWeb ? 24 : 16;
  const price = billingInterval === 'month' ? tier.monthlyPrice : tier.yearlyPrice;
  const monthly = billingInterval === 'year' ? Math.round(tier.yearlyPrice / 12) : tier.monthlyPrice;
  const isFree = price === 0;
  const isUpgrade = tier.monthlyPrice > currentTierPrice;

  const ctaLabel = isCurrentPlan
    ? 'Current Plan'
    : isFree
    ? 'Start Free'
    : currentTierPrice > 0
    ? isUpgrade ? `Upgrade to ${tier.name}` : `Switch to ${tier.name}`
    : `Get ${tier.name}`;

  const ctaBg = isCurrentPlan ? `${colors.traffic.green}26` : colors.primary[600];
  const ctaFg = isCurrentPlan ? colors.traffic.green : '#FFFFFF';

  return (
    <View style={equalHeight ? { flex: 1 } : undefined}>
      {/* Badge — above card */}
      {tier.badge && (
        <View style={{ alignItems: 'center', marginBottom: -12, zIndex: 1 }}>
          <TierBadge type={tier.badge} />
        </View>
      )}

      {/* Card */}
      <View style={{
        flex: equalHeight ? 1 : undefined,
        borderRadius: isWeb ? 20 : 16,
        backgroundColor: colors.surface.primary,
        borderColor: tier.highlighted ? colors.primary[500] : isCurrentPlan ? colors.traffic.green : colors.border.light,
        borderWidth: tier.highlighted || isCurrentPlan ? 2 : 1,
        opacity: disabled ? 0.6 : 1,
        padding: pad,
        paddingTop: tier.badge ? pad + 4 : pad,
      }}>
        {/* Name + tagline */}
        <Text style={{ fontSize: isWeb ? 22 : 18, fontWeight: '700', color: colors.text.primary, marginBottom: 2 }} numberOfLines={1}>
          {tier.name}
        </Text>
        <Text style={{ fontSize: 13, color: colors.text.secondary, marginBottom: isWeb ? 16 : 10 }} numberOfLines={1}>
          {tier.tagline}
        </Text>

        {/* Price */}
        {isFree ? (
          <Text style={{ fontSize: isWeb ? 44 : 28, fontWeight: '700', color: colors.text.primary, marginBottom: isWeb ? 12 : 8 }}>
            Free
          </Text>
        ) : (
          <View style={{ marginBottom: isWeb ? 12 : 8 }}>
            <View style={{ flexDirection: 'row', alignItems: 'baseline' }}>
              <Text style={{ fontSize: 14, fontWeight: '500', color: colors.text.secondary, marginRight: 4 }}>CHF</Text>
              <Text style={{ fontSize: isWeb ? 44 : 28, fontWeight: '700', color: colors.text.primary }}>{Math.round(monthly / 100)}</Text>
              <Text style={{ fontSize: 14, color: colors.text.tertiary, marginLeft: 4 }}>/mo</Text>
            </View>
            {billingInterval === 'year' && (
              <Text style={{ fontSize: 12, color: colors.text.tertiary, marginTop: 2 }}>Billed {formatPrice(price)} annually</Text>
            )}
          </View>
        )}

        {/* Description */}
        <Text style={{ fontSize: isWeb ? 14 : 13, lineHeight: isWeb ? 20 : 18, color: colors.text.secondary, marginBottom: isWeb ? 16 : 10 }} numberOfLines={2}>
          {tier.description}
        </Text>

        {/* Features */}
        {tier.features.map((feature, i) => (
          <View key={`f-${i}`} style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: isWeb ? 10 : 6 }}>
            <View style={{ width: 18, height: 18, borderRadius: 9, backgroundColor: `${colors.traffic.green}20`, alignItems: 'center', justifyContent: 'center' }}>
              <Check size={10} color={colors.traffic.green} strokeWidth={3} />
            </View>
            <Text style={{ fontSize: isWeb ? 14 : 13, color: colors.text.primary, flex: 1 }} numberOfLines={2}>
              {feature}
            </Text>
          </View>
        ))}

        {/* Spacer pins the CTA to the bottom when the card is stretched to equal height. No-op otherwise. */}
        {equalHeight && <View style={{ flexGrow: 1 }} />}

        {/* CTA Button — View wrapper with Pressable inside.
            Do NOT use Pressable with callback style — it doesn't compute
            height on RN 0.81 in some layout contexts. */}
        <View style={{
          height: isWeb ? 48 : 44,
          borderRadius: isWeb ? 12 : 10,
          backgroundColor: ctaBg,
          borderWidth: isCurrentPlan ? 1.5 : 0,
          borderColor: isCurrentPlan ? colors.traffic.green : undefined,
          marginTop: isWeb ? 16 : 10,
          overflow: 'hidden',
        }}>
          <Pressable
            onPress={onSelect}
            disabled={disabled || isCurrentPlan}
            accessibilityRole="button"
            accessibilityLabel={ctaLabel}
            style={{
              flex: 1,
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: disabled ? 0.5 : 1,
            }}
          >
            {isCurrentPlan && <Check size={16} color={ctaFg} strokeWidth={3} style={{ marginRight: 6 }} />}
            <Text style={{ fontSize: 15, fontWeight: '600', color: ctaFg }} numberOfLines={1}>
              {ctaLabel}
            </Text>
            {!isCurrentPlan && tier.highlighted && <Zap size={16} color="#fff" style={{ marginLeft: 6 }} />}
          </Pressable>
        </View>
      </View>
    </View>
  );
}
